/**
 * Pushable CTA styles for the public landing page.
 *
 * What: sx presets implementing the DESIGN.md §10 pushable button (solid offset
 * bottom shadow, translateY press, 80ms transitions) for the landing surface.
 * Why: The landing page is prerendered with hydration-parity contracts, so CTAs
 * must not branch styling in JS the way `PushButton` does with its
 * `useReducedMotion` hook — these presets keep the tactile mechanics in pure
 * CSS, with a `prefers-reduced-motion` media query carrying the fallback.
 * When: Update when DESIGN.md §10 button mechanics or the landing palette change.
 */
import type { SxProps, Theme } from '@mui/material/styles';

import { colors, radii } from '@/components/landing/landingTokens';

const EASE = 'cubic-bezier(0.2, 0.9, 0.25, 1)';

/**
 * The §10 vertical padding (16px), and it is load-bearing beyond the spec:
 * `theme.ts` gives `MuiButton.root` a `@media (min-width:900px) { min-height:
 * 44px }` desktop-workspace floor, and emotion emits a class's nested at-rules
 * *after* its flat declarations — so that media query outranks a flat
 * `minHeight` in `sx`, and a preset sized only by `minHeight: 56` renders 44px
 * from md up. Padding the label to full height keeps the height intrinsic,
 * where the floor can't reach it. Don't shrink this (or the label) without
 * restating the height at the same breakpoint, the way `pushableCompactSx`
 * has to.
 */
const PUSHABLE_PY = 2;

const pushableBase = {
  borderRadius: radii.pill,
  textTransform: 'none',
  fontWeight: 800,
  letterSpacing: '-0.01em',
  // The pair renders as a <button> (primary, dialog) beside an <a> (secondary,
  // route). The UA sheet centers button text and leaves anchor text inheriting
  // `start`, so a label that wraps came out centered on one pill and ragged-left
  // on the other. Pin it for both.
  textAlign: 'center',
  lineHeight: 1.3,
  transition: `transform 80ms ${EASE}, box-shadow 80ms ${EASE}, background-color 150ms`,
  outline: '2px solid transparent',
  outlineOffset: 3,
  '&:focus-visible': {
    outline: `3px solid ${colors.accentCobalt}`,
  },
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'background-color 150ms',
    '&:hover': { transform: 'none' },
    '&:active': { transform: 'none' },
  },
} as const;

/**
 * Orange primary CTA — brand action. Label is 19px because white-on-orange
 * measures 3.1:1 and passes WCAG only as large text (bold ≥19px) — §10.
 */
export const pushablePrimarySx: SxProps<Theme> = {
  ...pushableBase,
  px: { xs: 3, sm: 3.75 },
  py: PUSHABLE_PY,
  minHeight: 56,
  fontSize: 19,
  color: '#FFFFFF',
  backgroundColor: colors.accentOrange,
  boxShadow: `0 4px 0 ${colors.accentOrangeDark}`,
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: `0 5px 0 ${colors.accentOrangeDark}`,
    backgroundColor: colors.accentOrangeLight,
  },
  '&:active': {
    transform: 'translateY(2px)',
    boxShadow: `0 2px 0 ${colors.accentOrangeDark}`,
  },
};

/** Cobalt-outline secondary CTA on paper. */
export const pushableSecondarySx: SxProps<Theme> = {
  ...pushableBase,
  px: { xs: 3, sm: 3.25 },
  py: PUSHABLE_PY,
  minHeight: 56,
  fontSize: 16,
  color: colors.accentCobalt,
  backgroundColor: colors.paper,
  border: `2px solid ${colors.accentCobalt}`,
  boxShadow: `0 4px 0 ${colors.accentCobaltDark}`,
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: `0 5px 0 ${colors.accentCobaltDark}`,
    backgroundColor: colors.paper,
    border: `2px solid ${colors.accentCobalt}`,
  },
  '&:active': {
    transform: 'translateY(2px)',
    boxShadow: `0 2px 0 ${colors.accentCobaltDark}`,
  },
};

/**
 * Compact orange pill for tight slots (product-card foot). At 13px the label
 * can't qualify as WCAG large text, so it takes ink (5.3:1 on orange), not
 * white — the §10 small-orange rule.
 */
export const pushableCompactSx: SxProps<Theme> = {
  ...pushableBase,
  px: 2,
  minHeight: 36,
  // 36px sits *below* the theme's 44px desktop floor, so no amount of padding
  // can hold it the way the full-size presets do. Today's only consumer is a
  // `Box`, which the `MuiButton` override never reaches — this keeps the pill
  // 36px the day it lands on a real `Button`.
  '@media (min-width:900px)': { minHeight: 36 },
  fontSize: 13,
  color: colors.ink,
  backgroundColor: colors.accentOrange,
  boxShadow: `0 3px 0 ${colors.accentOrangeDark}`,
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: `0 4px 0 ${colors.accentOrangeDark}`,
    backgroundColor: colors.accentOrangeLight,
  },
  '&:active': {
    transform: 'translateY(1px)',
    boxShadow: `0 2px 0 ${colors.accentOrangeDark}`,
  },
};
